import { useCallback, useEffect, useState } from 'react';
import { Maybe } from '@sso/common-types';
import { logTraffic } from '@sso/traffic';

import { getBrowserGlobals } from '../helpers/globals';
import { performFormAutoSubmission } from '../helpers/formHelper';

const GOOGLE_ONE_TAP_SCRIPT_URL = 'https://accounts.google.com/gsi/client';

let loadPromise: Maybe<Promise<void>> = null;
const loadOneTapAndExec = async (callback?: () => void) => {
  if (loadPromise) {
    await loadPromise;
    return callback && callback();
  }

  loadPromise = new Promise((resolve, reject) => {
    const browserGlobals = getBrowserGlobals();
    if (browserGlobals == null) {
      return reject(new Error('loadOneTapAndExec expected to work on client-side not ssr'));
    }
    const { document } = browserGlobals;
    const script = document.createElement('script');
    script.defer = true;
    script.async = true;

    script.onload = () => {
      try {
        callback && callback();
        return resolve();
      } catch (e) {
        return reject(e);
      }
    };

    script.src = GOOGLE_ONE_TAP_SCRIPT_URL;
    document.head.appendChild(script);
  });

  return loadPromise;
};

type Response = {
  credential: string;
};

type Notification = {
  isDisplayed: () => boolean;
  isNotDisplayed: () => boolean;
  isSkippedMoment: () => boolean;
  isDisplayMoment: () => boolean;
  isDismissedMoment: () => boolean;
  getNotDisplayedReason: () => string;
  getSkippedReason: () => string;
  getDismissedReason: () => string;
};

type Props = {
  showOneTap: boolean;
  oneTapType: string;
  onOneTapClose: (closed: boolean) => void;
  submitUrl: string;
  traffic?: (event: string, trafficEvent: string) => void;
  traffic_prefix?: string;
  host: string;
};

export const GoogleOneTap = ({
  showOneTap,
  host,
  oneTapType,
  submitUrl,
  traffic,
  onOneTapClose,
  traffic_prefix: trafficPrefix,
}: Props) => {
  const [initialized, setInitialized] = useState<boolean>(false);

  useEffect(() => {
    // performance optimization: prefetch google one tap script.
    loadOneTapAndExec();
  }, []);

  const removeOneTapFocus = () => {
    const browserGlobals = getBrowserGlobals();
    if (browserGlobals == null) {
      return;
    }
    const { window, document } = browserGlobals;
    window.setTimeout(() => {
      const onetapIframe = document.querySelector<HTMLIFrameElement>('#google-one-tap iframe');
      onetapIframe && onetapIframe.blur();
    }, 500);
  };

  const onOneTapNotification = useCallback(
    (notification: Notification) => {
      const isOneTapClosed = notification.isNotDisplayed() || notification.isSkippedMoment();
      onOneTapClose(isOneTapClosed);

      if (!traffic) {
        return;
      }

      const prefix = trafficPrefix != null ? `${trafficPrefix}.` : '';

      if (notification.isDisplayMoment()) {
        if (notification.isDisplayed()) {
          removeOneTapFocus();
          traffic('event', `${prefix}event.onetap.displayed`);
        }
        if (notification.isNotDisplayed()) {
          traffic('event', `${prefix}event.onetap.not_displayed.${notification.getNotDisplayedReason()}`);
        }
      }
      if (notification.isSkippedMoment()) {
        traffic('event', `${prefix}event.onetap.skipped.${notification.getSkippedReason()}`);
      }
      if (notification.isDismissedMoment()) {
        traffic('event', `${prefix}event.onetap.dismissed.${notification.getDismissedReason()}`);
      }
    },
    [traffic, onOneTapClose, trafficPrefix],
  );

  const submitOneTapForm = (url: string, data: Record<string, string | null>) => {
    const browserGlobals = getBrowserGlobals();
    if (browserGlobals == null) {
      return;
    }
    const { document } = browserGlobals;
    const form = document.createElement('form');
    form.id = 'google-onetap-form-submit';
    form.method = 'POST';
    form.action = url;

    for (const name in data) {
      if (Object.hasOwn(data, name)) {
        const input = document.createElement('input');
        input.type = 'hidden';
        input.name = name;
        input.value = data[name] ?? '';
        form.appendChild(input);
      }
    }

    performFormAutoSubmission(form.outerHTML, form.id, url);
  };

  const responseCallback = useCallback(
    (response: Response) => {
      const oneTapIdToken = response.credential;

      const url = new URL(submitUrl);
      const stateParams = url.searchParams.get('state');
      url.searchParams.delete('state');
      url.searchParams.delete('clientId');

      const data = {
        code: '',
        state: stateParams,
        oneTapIdToken,
      };

      submitOneTapForm(url.toString(), data);
    },
    [submitUrl],
  );

  const onLoadInitialize = useCallback(() => {
    const url = new URL(submitUrl);
    const clientId = url.searchParams.get('client_id');
    const browserGlobals = getBrowserGlobals();
    if (browserGlobals == null) {
      return;
    }
    const { window } = browserGlobals;
    if (window.google == null) {
      return;
    }
    window.google.accounts.id.initialize({
      client_id: clientId,
      prompt_parent_id: oneTapType === 'inline' ? 'google-one-tap' : undefined,
      context: 'signup',
      state_cookie_domain: host,
      cancel_on_tap_outside: false,
      callback: responseCallback,
    });

    window.google.accounts.id.prompt(onOneTapNotification);

    setInitialized(true);
  }, [onOneTapNotification, responseCallback, host, oneTapType, submitUrl]);

  useEffect(() => {
    if (showOneTap && submitUrl && !initialized) {
      loadOneTapAndExec(() => {
        try {
          onLoadInitialize();
        } catch (e) {
          logTraffic('impression', 'sso.google_one_tap.script.load.error');
        }
      });
    }
  }, [initialized, showOneTap, submitUrl, onLoadInitialize]);

  return <>{showOneTap && <div id='google-one-tap' />}</>;
};
